import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import api from "../../../services/api";

export default function RolesAccessTab() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMatrixData();
  }, []);

  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/role-access/matrix-data');
      if (res.data.success) {
        setRoles(res.data.data.roles);
        setModules(res.data.data.modules);
        setMatrix(res.data.data.matrix);
      }
    } catch (error) {
      toast.error("Failed to load roles and permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (roleKey, moduleName, permissionType) => {
    if (roleKey === 'owner') return; // Owner permissions cannot be changed
    setMatrix(prev => ({
      ...prev,
      [roleKey]: {
        ...prev[roleKey],
        [moduleName]: {
          ...prev[roleKey]?.[moduleName],
          [permissionType]: !prev[roleKey]?.[moduleName]?.[permissionType]
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Save all roles sequentially (or run Promise.all)
      // Filter out owner as we don't need to save owner's permissions (always true)
      const rolesToSave = roles.filter(r => r.key !== 'owner');
      
      const promises = rolesToSave.map(role => {
        const permissions = modules.map(mod => ({
          module: mod,
          view: matrix[role.key]?.[mod]?.view || false,
          edit: matrix[role.key]?.[mod]?.edit || false,
          approve: matrix[role.key]?.[mod]?.approve || false,
          export: matrix[role.key]?.[mod]?.export || false,
        }));
        return api.put('/role-access', { role: role.key, permissions });
      });

      await Promise.all(promises);
      toast.success("Role access matrix saved successfully!");
    } catch (error) {
      toast.error("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px]">
      <div className="card p-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="section-title">Roles & Permissions Matrix</h2>
          <div className="flex items-center gap-5 text-[13px] text-[#626262]">
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 border border-slate-300 rounded-sm inline-block"></span>
              <span>View</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 border border-slate-300 rounded-sm inline-block"></span>
              <span>Edit</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 border border-slate-300 rounded-sm inline-block"></span>
              <span>Approve</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 border border-slate-300 rounded-sm inline-block"></span>
              <span>Export/Download</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto custom-scrollbar pb-2">
          <table className="w-full text-left border-collapse min-w-[1000px]">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white z-10 min-w-[140px] font-semibold text-slate-700 py-3 border-b">Module</th>
                {roles.map((role) => (
                  <th key={role.key} className="text-center min-w-[120px] px-2 font-semibold text-slate-700 py-3 border-b">{role.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((mod, rowIndex) => (
                <tr key={rowIndex} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                  <td className="font-medium text-slate-700 py-3 sticky left-0 bg-white group-hover:bg-slate-50/50 z-10 shadow-[1px_0_0_#ECE7DE]">{mod}</td>
                  {roles.map((role) => {
                    const isOwner = role.key === "owner";
                    const p = matrix[role.key]?.[mod] || {};
                    return (
                      <td key={role.key} className="text-center px-2 py-3">
                        <div className="flex items-center gap-1.5 justify-center">
                          <input 
                            type="checkbox" 
                            title="View" 
                            checked={p.view || false} 
                            disabled={isOwner}
                            onChange={() => handleToggle(role.key, mod, 'view')}
                            className="w-3.5 h-3.5 accent-brand-blue cursor-pointer shrink-0" 
                          />
                          <input 
                            type="checkbox" 
                            title="Edit" 
                            checked={p.edit || false} 
                            disabled={isOwner}
                            onChange={() => handleToggle(role.key, mod, 'edit')}
                            className="w-3.5 h-3.5 accent-brand-blue cursor-pointer shrink-0" 
                          />
                          <input 
                            type="checkbox" 
                            title="Approve" 
                            checked={p.approve || false} 
                            disabled={isOwner}
                            onChange={() => handleToggle(role.key, mod, 'approve')}
                            className="w-3.5 h-3.5 accent-brand-blue cursor-pointer shrink-0" 
                          />
                          <input 
                            type="checkbox" 
                            title="Export/Download" 
                            checked={p.export || false} 
                            disabled={isOwner}
                            onChange={() => handleToggle(role.key, mod, 'export')}
                            className="w-3.5 h-3.5 accent-brand-blue cursor-pointer shrink-0" 
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer info and Action */}
        <div className="mt-8 flex items-center justify-between">
          <p className="text-[13px] text-[#626262]">
            Unchecked rows are hidden entirely from this role's menu, not just disabled.
          </p>
          <button 
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? "Saving..." : "Save Role Access"}
          </button>
        </div>

      </div>
    </div>
  );
}




